import { Component, Input } from '@angular/core';
import { ApiService } from '../../services/api/api.service';
import { AbstractControl, FormBuilder, Validators, ControlValueAccessor, NG_VALUE_ACCESSOR, Validator, NG_VALIDATORS, ValidationErrors, AbstractControlOptions } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormHelper } from '../../helpers/form.helper';
import { PasswordEvaluation } from '../../types/password-evaluation.type';

@Component({
  selector: 'shared-password-definition',
  templateUrl: './password-definition.component.html',
  styleUrl: './password-definition.component.scss',
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: PasswordDefinitionComponent, multi: true },
    { provide: NG_VALIDATORS, multi: true, useExisting: PasswordDefinitionComponent },
  ],
  standalone: false,
})
export class PasswordDefinitionComponent implements ControlValueAccessor, Validator {
  @Input() passwordLabel = 'Choose your password';
  @Input() passwordConfirmationLabel = 'Enter your password again';

  passwordEval: PasswordEvaluation;

  form = this.formBuilder.group({
    password: ['', {
      validators: [Validators.required],
      asyncValidators: [this.passwordAsyncValidator.bind(this)],
    } as AbstractControlOptions],
    passwordConfirmation: ['', []],
  }, {
    validators: (f: AbstractControl) => this.checkPasswordsEqual(f),
  });

  get f() {
    return this.form.controls;
  }

  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => { };
  private onValidatorChange: () => void = () => {};

  constructor(private apiService: ApiService, private formBuilder: FormBuilder) {
    this.form.valueChanges.pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.onTouched();
        this.onChange(this.f.password.value);
      });
  }

  fixSentence(sentence: string): string {
    return sentence.endsWith('.') ? sentence : `${sentence}.`;
  }

  isFieldInvalid(field: string, validation: string = null): boolean {
    return FormHelper.isFieldInvalid(this.form, field, [validation]);
  }

  private async passwordAsyncValidator(control: AbstractControl): Promise<ValidationErrors | null> {
    const password = control.value as string;
    if (!password || password.length < 8 || password.length > 64) {
      this.passwordEval = {
        score: !password ? null : password.length < 8 ? Math.round(password.length / 3) : 0,
        suggestions: [],
        warning: null,
      };
    } else {
      this.passwordEval = await this.apiService.auth.evaluatePassword(password);
    }

    if (this.passwordEval.score < 3) {
      return { weekPassword: true };
    }
    return null;
  }

  writeValue(value: string): void {
    this.f.password.setValue(value ?? '', { emitEvent: false });
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.form.disable();
    } else {
      this.form.enable();
    }
  }

  private checkPasswordsEqual(form: AbstractControl): ValidationErrors {
    const f = form as typeof this.form;
    const password = f.controls.password.value;
    const passwordConfirmation = f.controls.passwordConfirmation.value;
    const errors = password === passwordConfirmation ? null : { passwordMismatch: true };
    f.controls.passwordConfirmation.setErrors(errors);
    return errors;
  }

  validate(control: AbstractControl): ValidationErrors | null {
    return this.form.invalid
      ? { ...this.f.password.errors, ...this.f.passwordConfirmation.errors }
      : null;
  }

  registerOnValidatorChange?(fn: () => void): void {
    this.onValidatorChange = fn;
  }
}
